import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Message } from "../types/tutor.types";

export interface TutorChatStoreState {
  // 상태값
  history: Message[];
  searchContent: string;
  fetching: boolean;
  pieceDataSum: string;

  // 스트리밍 ref 대체 (전역)
  streamingMessageId: string | null;
  abortController: AbortController | null;

  // 메시지 액션
  addMessage: (message: Message) => void;
  addMessages: (messages: Message[]) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  removeMessage: (messageId: string) => void;
  setHistory: (history: Message[]) => void;
  clearHistory: () => void;

  // 스트리밍 액션
  startStreamingMessage: (messageId: string, groupId: string) => void;
  appendStreamChunk: (messageId: string, chunk: string) => void;
  completeStreamingMessage: (messageId: string) => void;
  abortStreamingMessage: (messageId: string, error?: string) => void;

  // 스트리밍 ref 액션
  setStreamingMessageId: (id: string | null) => void;
  setAbortController: (controller: AbortController | null) => void;
  cancelStreaming: () => void;

  // 기타 액션
  setSearchContent: (searchContent: string) => void;
  setFetching: (fetching: boolean) => void;
  setPieceDataSum: (data: string) => void;
}

/**
 * 튜터 채팅 스토어
 * - 튜터 모드의 채팅 메시지 관리
 * - 스트리밍 응답 처리
 * - 검색 기능
 */
export const useTutorChatStore = create<TutorChatStoreState>()(
  devtools(
    (set, get) => ({
      // ============================================
      // 초기 상태
      // ============================================
      history: [],
      searchContent: "",
      fetching: false,
      pieceDataSum: "",
      streamingMessageId: null,
      abortController: null,

      // ============================================
      // 메시지 관리 액션
      // ============================================

      // 단일 메시지 추가
      addMessage: (message: Message) => {
        set((state) => ({
          history: [...state.history, message],
        }));
      },

      // 여러 메시지 추가
      addMessages: (messages: Message[]) => {
        set((state) => ({
          history: [...state.history, ...messages],
        }));
      },

      // 특정 메시지 업데이트
      updateMessage: (messageId: string, updates: Partial<Message>) => {
        set((state) => ({
          history: state.history.map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ),
        }));
      },

      // 특정 메시지 제거 (백엔드 저장 후 스트리밍 메시지 정리용)
      removeMessage: (messageId: string) => {
        set((state) => ({
          history: state.history.filter((msg) => msg.id !== messageId),
        }));
      },

      // 히스토리 전체 교체
      setHistory: (history: Message[]) => set({ history }),

      // 히스토리 초기화
      clearHistory: () => set({ history: [] }),

      // ============================================
      // 스트리밍 전용 액션
      // ============================================

      /**
       * 스트리밍 메시지 시작
       * - AI 메시지를 빈 내용으로 생성하고 is_loading = true로 설정
       */
      startStreamingMessage: (messageId: string, groupId: string) => {
        const newMessage: Message = {
          id: messageId,
          group_id: groupId,
          content: "",
          sender: "ai",
          created_at: new Date().toISOString(),
          is_loading: true,
        };

        set((state) => ({
          history: [...state.history, newMessage],
          fetching: true,
          pieceDataSum: "", // 청크 누적용 초기화
        }));
      },

      /**
       * 스트리밍 청크 추가
       * - 해당 메시지의 content에 청크를 추가
       * - pieceDataSum에도 누적 (레거시 호환성)
       */
      appendStreamChunk: (messageId: string, chunk: string) => {
        set((state) => {
          const updatedHistory = state.history.map((msg) =>
            msg.id === messageId
              ? { ...msg, content: msg.content + chunk }
              : msg
          );

          return {
            history: updatedHistory,
            pieceDataSum: state.pieceDataSum + chunk,
          };
        });
      },

      /**
       * 스트리밍 완료
       * - is_loading을 false로 변경
       * - fetching 상태 해제
       */
      completeStreamingMessage: (messageId: string) => {
        set((state) => ({
          history: state.history.map((msg) =>
            msg.id === messageId ? { ...msg, is_loading: false } : msg
          ),
          fetching: false,
        }));
      },

      /**
       * 스트리밍 중단/실패
       * - error 메시지 설정
       * - is_loading을 false로 변경
       * - fetching 상태 해제
       * - content가 비어있으면 fallback 메시지 표시
       */
      abortStreamingMessage: (messageId: string, error?: string) => {
        set((state) => ({
          history: state.history.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  is_loading: false,
                  // content가 비어있으면 fallback 메시지 (레거시 호환)
                  content: msg.content || "다시 시도해주세요.",
                  error: error || "응답 생성 중 문제가 발생했습니다.",
                }
              : msg
          ),
          fetching: false,
        }));
      },

      // ============================================
      // 스트리밍 ref 액션
      // ============================================

      setStreamingMessageId: (id: string | null) => set({ streamingMessageId: id }),

      setAbortController: (controller: AbortController | null) =>
        set({ abortController: controller }),

      /**
       * 스트리밍 취소 통합 함수
       * - HTTP 요청 중단은 tutorApiService.cancelStreaming()에서 처리
       * - 여기서는 스토어 상태만 정리
       */
      cancelStreaming: () => {
        set({
          abortController: null,
          streamingMessageId: null,
        });
      },

      // ============================================
      // 기타 액션
      // ============================================

      // 검색어 설정
      setSearchContent: (searchContent: string) => set({ searchContent }),

      // 통신 상태 업데이트
      setFetching: (fetching: boolean) => set({ fetching }),

      // 스트리밍 데이터 누적 (레거시 호환성)
      setPieceDataSum: (data: string) => set({ pieceDataSum: data }),
    }),
    { name: "TutorChatStore" } // Redux DevTools 이름
  )
);
